import React, { useState, useEffect } from 'react';
import { X, User, Lock, Save, Loader, AlertCircle, CheckCircle, Eye, EyeOff, KeyRound } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useAuthStore } from '../store/authStore';

export default function ProfileModal({ open, onClose }) {
  // --- 1. HOOKS ---
  const { user, setSession } = useAuthStore();
  const [activeTab, setActiveTab] = useState('details');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [formData, setFormData] = useState({
    name: '',
    gender: 'Non-Binary',
    email: '',
  });

  const [passData, setPassData] = useState({
    currentPassword: '', // New field
    newPassword: '',
    confirmPassword: ''
  });
  
  const [showPass, setShowPass] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.user_metadata?.full_name || '',
        gender: user.user_metadata?.gender || 'Non-Binary',
        email: user.email || ''
      });
    }
  }, [user]);

  // --- 2. EARLY RETURN ---
  if (!open) return null;

  // --- ACTIONS ---

  // Handle Forgot Password
  const handleForgotPassword = async () => {
    if (!user?.email) return;
    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
        redirectTo: window.location.origin, // Redirects back to your app
      });
      if (error) throw error;
      setMessage({ type: 'success', text: 'Password reset email sent! Check your inbox.' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const { data, error } = await supabase.auth.updateUser({
        data: {
          full_name: formData.name,
          gender: formData.gender
        }
      });
      if (error) throw error;

      if (formData.email !== user.email) {
        const { error: emailError } = await supabase.auth.updateUser({
          email: formData.email
        });
        if (emailError) throw emailError;
        setMessage({ type: 'success', text: 'Profile updated! Check email to confirm.' });
      } else {
        setMessage({ type: 'success', text: 'Profile updated successfully.' });
      }

      if(data.session) setSession(data.session);

    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    // 1. Basic Validation
    if (!passData.currentPassword) {
      return setMessage({ type: 'error', text: 'Please enter your current password.' });
    }
    if (passData.newPassword.length < 6) {
      return setMessage({ type: 'error', text: 'New password must be at least 6 characters.' });
    }
    if (passData.newPassword !== passData.confirmPassword) {
      return setMessage({ type: 'error', text: 'New passwords do not match.' });
    }

    setLoading(true);
    try {
      // 2. Security Check: Verify Old Password by trying to sign in
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: passData.currentPassword
      });

      if (signInError) {
        throw new Error("Current password is incorrect.");
      }

      // 3. Update to New Password
      const { error } = await supabase.auth.updateUser({
        password: passData.newPassword
      });
      if (error) throw error;

      setMessage({ type: 'success', text: 'Password changed successfully!' });
      setPassData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[3000] flex items-end justify-center sm:items-center">
      
      <div 
        className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity" 
        onClick={onClose}
      />
      
      <div className="relative w-full max-w-md bg-[#FBF5DE] rounded-t-2xl sm:rounded-2xl shadow-2xl p-6 animate-[slideUp_0.3s_ease-out] max-h-[90vh] overflow-y-auto border border-[#B6A875]">
        
        <button onClick={onClose} className="absolute top-4 right-4 text-[#6F7565] hover:text-[#394A3C]">
          <X className="w-6 h-6" />
        </button>

        <h2 className="text-2xl font-dunhill font-bold text-[#394A3C] mb-1">Profile Settings</h2>
        <p className="text-xs text-[#6F7565] mb-6">Update your personal druid details.</p>

        {/* Tabs */}
        <div className="flex p-1 bg-[#EFE4C2] rounded-xl mb-6">
          <button
            onClick={() => { setActiveTab('details'); setMessage({type:'', text:''}); }}
            className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all ${
              activeTab === 'details' ? 'bg-white text-[#394A3C] shadow-sm' : 'text-[#6F7565] hover:text-[#394A3C]'
            }`}
          >
            Details
          </button>
          <button
            onClick={() => { setActiveTab('security'); setMessage({type:'', text:''}); }}
            className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all ${
              activeTab === 'security' ? 'bg-white text-[#394A3C] shadow-sm' : 'text-[#6F7565] hover:text-[#394A3C]'
            }`}
          >
            Security
          </button>
        </div>

        {/* Messages */}
        {message.text && (
          <div className={`mb-4 p-3 rounded-xl flex gap-3 text-sm animate-[fadeIn_0.2s_ease-out] ${
            message.type === 'error' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200'
          }`}>
            {message.type === 'error' ? <AlertCircle className="w-5 h-5 flex-shrink-0" /> : <CheckCircle className="w-5 h-5 flex-shrink-0" />}
            <span>{message.text}</span>
          </div>
        )}

        {/* --- DETAILS TAB --- */}
        {activeTab === 'details' && (
          <form onSubmit={handleUpdateProfile} className="space-y-4">
            <div className="space-y-1">
              <label className="text-xs font-bold text-[#394A3C] uppercase tracking-wider">Display Name</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A0A0A0]" />
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full pl-9 pr-4 py-3 bg-white border border-[#D0C8B0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#6FA58E] text-[#394A3C]"
                  placeholder="Your Name"
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-bold text-[#394A3C] uppercase tracking-wider">Druid Type (Gender)</label>
              <div className="relative">
                <select
                  value={formData.gender}
                  onChange={(e) => setFormData({...formData, gender: e.target.value})}
                  className="w-full pl-4 pr-4 py-3 bg-white border border-[#D0C8B0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#6FA58E] text-[#394A3C] appearance-none"
                >
                  <option value="Non-Binary">Non-Binary</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
                <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-[#A0A0A0]">▼</div>
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-bold text-[#394A3C] uppercase tracking-wider">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full px-4 py-3 bg-white border border-[#D0C8B0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#6FA58E] text-[#394A3C]"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full mt-4 py-3 bg-[#6FA58E] hover:bg-[#5E947D] text-[#1F2A24] font-bold rounded-xl shadow-md transition-transform active:scale-[0.98] flex items-center justify-center gap-2"
            >
              {loading ? <Loader className="w-5 h-5 animate-spin" /> : <> <Save className="w-4 h-4" /> Save Changes </>}
            </button>
          </form>
        )}

        {/* --- SECURITY TAB --- */}
        {activeTab === 'security' && (
          <form onSubmit={handleChangePassword} className="space-y-5">
            
            {/* 1. CURRENT PASSWORD */}
            <div className="space-y-1">
              <div className="flex justify-between items-center">
                <label className="text-xs font-bold text-[#394A3C] uppercase tracking-wider">Current Password</label>
                <button 
                  type="button" 
                  onClick={handleForgotPassword}
                  className="text-[10px] text-[#6FA58E] hover:underline hover:text-[#5E947D] font-bold"
                >
                  Forgot Password?
                </button>
              </div>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A0A0A0]" />
                <input
                  type={showPass ? "text" : "password"}
                  value={passData.currentPassword}
                  onChange={(e) => setPassData({...passData, currentPassword: e.target.value})}
                  className="w-full pl-9 pr-10 py-3 bg-white border border-[#D0C8B0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#6FA58E] text-[#394A3C]"
                  placeholder="Enter current password"
                />
              </div>
            </div>

            <hr className="border-[#D0C8B0]/50" />

            {/* 2. NEW PASSWORD */}
            <div className="space-y-1">
              <label className="text-xs font-bold text-[#394A3C] uppercase tracking-wider">New Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A0A0A0]" />
                <input
                  type={showPass ? "text" : "password"}
                  value={passData.newPassword}
                  onChange={(e) => setPassData({...passData, newPassword: e.target.value})}
                  className="w-full pl-9 pr-10 py-3 bg-white border border-[#D0C8B0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#6FA58E] text-[#394A3C]"
                  placeholder="Min 6 characters"
                />
                <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A0A0A0] hover:text-[#394A3C]">
                  {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* 3. CONFIRM PASSWORD */}
            <div className="space-y-1">
              <label className="text-xs font-bold text-[#394A3C] uppercase tracking-wider">Confirm New Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A0A0A0]" />
                <input
                  type={showPass ? "text" : "password"}
                  value={passData.confirmPassword}
                  onChange={(e) => setPassData({...passData, confirmPassword: e.target.value})}
                  className="w-full pl-9 pr-4 py-3 bg-white border border-[#D0C8B0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#6FA58E] text-[#394A3C]"
                  placeholder="Re-enter new password"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-[#6FA58E] hover:bg-[#5E947D] text-[#1F2A24] font-bold rounded-xl shadow-md transition-transform active:scale-[0.98] flex items-center justify-center gap-2"
            >
              {loading ? <Loader className="w-5 h-5 animate-spin" /> : 'Update Password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}